import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { TemplateField, FieldType } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

export function renderTemplateBuilder(container: HTMLElement, navigate: NavigateFn, params: Record<string, string> = {}): void {
  const session = getSession();
  const industry = session?.industry ?? 'general';

  const constructionQuickAdd = ['Contractor Name', 'License Number', 'Project Address', 'Project Name', 'Contract Amount', 'Start Date', 'Completion Date', 'Scope of Work', 'Change Order #', 'Client Name', 'Signature', 'Materials List'];
  const restaurantQuickAdd = ['Employee Name', 'Shift Date', 'Start Time', 'End Time', 'Position', 'Tips Received', 'Signature', 'Manager Approval', 'Notes'];
  const generalQuickAdd = ['Signature', 'Date', 'Project Name', 'Client Name', 'Amount ($)', 'Phone', 'Address', 'Notes'];
  const quickAddItems = industry === 'construction' ? constructionQuickAdd : industry === 'restaurant' ? restaurantQuickAdd : generalQuickAdd;

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;align-items:center;gap:12px">
        <button class="btn btn-ghost btn-sm" id="back-btn">← Library</button>
        <h1 class="page-title" style="margin:0">${params.templateId ? 'Edit' : 'Build'} Template</h1>
      </div>
    </div>

    <!-- AI Template Generator -->
    <div class="card" style="margin-bottom:20px;border-left:4px solid var(--brand-gold)">
      <div style="display:flex;align-items:flex-start;gap:12px">
        <i class="bi bi-stars" style="font-size:22px;color:var(--brand-gold);flex-shrink:0;margin-top:2px"></i>
        <div style="flex:1">
          <div style="font-size:13px;font-weight:700;margin-bottom:4px">AI Template Generator</div>
          <div style="font-size:12px;color:var(--text-muted);margin-bottom:12px">
            Describe the document you need and AI will generate the fields. Works great for ${industry === 'construction' ? 'construction contracts, lien waivers, change orders, safety forms' : industry === 'restaurant' ? 'employee agreements, tip reports, health checklists' : 'business agreements, invoices, NDAs'}.
          </div>
          <div style="display:flex;gap:10px;align-items:flex-end">
            <div style="flex:1">
              <input type="text" id="ai-template-prompt"
                placeholder="${industry === 'construction' ? 'e.g. Subcontractor lien waiver with conditional and unconditional sections' : industry === 'restaurant' ? 'e.g. Daily tip report for servers with per-table breakdown' : 'e.g. Non-disclosure agreement for freelance consultants'}"
                style="width:100%" />
            </div>
            <button class="btn btn-gold" id="ai-gen-template-btn" style="white-space:nowrap;flex-shrink:0">
              <i class="bi bi-cpu"></i> Generate Fields
            </button>
          </div>
        </div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 380px;gap:20px">
      <!-- Left: template config -->
      <div>
        <div class="card" style="margin-bottom:16px">
          <h3 style="font-size:13px;font-weight:700;margin-bottom:16px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Template Info</h3>
          <div class="form-row">
            <label>Template Name *</label>
            <input type="text" id="tmpl-name" placeholder="e.g. Custom Change Order — Interior Work" />
          </div>
          <div class="form-row-2">
            <div>
              <label>Category</label>
              <input type="text" id="tmpl-category" placeholder="Contracts, HR, Legal…" />
            </div>
            <div>
              <label>Industry</label>
              <select id="tmpl-industry">
                <option value="all">All Industries</option>
                <option value="construction">Construction</option>
                <option value="restaurant">Restaurant</option>
                <option value="general">General</option>
              </select>
            </div>
          </div>
          <div class="form-row" style="margin-bottom:0">
            <label>Description</label>
            <input type="text" id="tmpl-desc" placeholder="Brief description of this template…" />
          </div>
        </div>

        <div class="card">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
            <h3 style="font-size:13px;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Fields</h3>
            <span style="font-size:11px;color:var(--text-muted)" id="field-count">0 fields</span>
          </div>

          <div id="field-list" style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px">
            <div class="empty-state" style="padding:24px">
              <div style="font-size:13px;color:var(--text-muted)">Add fields from the panel →</div>
            </div>
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end">
            <button class="btn btn-ghost" id="back-btn-2">Cancel</button>
            <button class="btn btn-gold btn-lg" id="save-btn">Save Template</button>
          </div>
        </div>
      </div>

      <!-- Right: add field panel -->
      <div class="card" style="align-self:start;position:sticky;top:20px">
        <h3 style="font-size:13px;font-weight:700;margin-bottom:16px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:1px">Add Field</h3>
        <div class="form-row">
          <label>Field Label *</label>
          <input type="text" id="field-label" placeholder="e.g. Project Name" />
        </div>
        <div class="form-row">
          <label>Field Type</label>
          <select id="field-type">
            <option value="text">Short Text</option>
            <option value="textarea">Long Text / Paragraph</option>
            <option value="number">Number</option>
            <option value="currency">Currency ($)</option>
            <option value="date">Date</option>
            <option value="select">Dropdown List</option>
            <option value="signature">Signature</option>
          </select>
        </div>
        <div class="form-row" id="options-row" style="display:none">
          <label>Options (one per line)</label>
          <textarea id="field-options" style="min-height:80px" placeholder="Option A&#10;Option B&#10;Option C"></textarea>
        </div>
        <div class="form-row">
          <label>Placeholder / Hint</label>
          <input type="text" id="field-placeholder" placeholder="Helpful hint text…" />
        </div>
        <div class="form-row-2">
          <div>
            <label>Width</label>
            <select id="field-width">
              <option value="full">Full width</option>
              <option value="half">Half width</option>
            </select>
          </div>
          <div style="display:flex;align-items:flex-end;padding-bottom:2px">
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;font-weight:500">
              <input type="checkbox" id="field-required" checked style="width:16px;height:16px" />
              Required
            </label>
          </div>
        </div>

        <button class="btn btn-primary" style="width:100%" id="add-field-btn">+ Add Field</button>

        <div style="border-top:1px solid var(--border);margin-top:20px;padding-top:16px">
          <div style="font-size:11px;color:var(--text-muted);margin-bottom:10px;font-weight:600">QUICK ADD</div>
          <div style="display:flex;flex-wrap:wrap;gap:6px" id="quick-add-list">
          </div>
        </div>
      </div>
    </div>
  `;

  document.getElementById('back-btn')!.addEventListener('click', () => navigate('templates'));
  document.getElementById('back-btn-2')!.addEventListener('click', () => navigate('templates'));

  document.getElementById('field-type')!.addEventListener('change', (e) => {
    const type = (e.target as HTMLSelectElement).value;
    document.getElementById('options-row')!.style.display = type === 'select' ? 'block' : 'none';
  });

  const fields: TemplateField[] = [];

  // Populate quick-add chips
  const quickAddEl = document.getElementById('quick-add-list')!;
  quickAddEl.innerHTML = quickAddItems.map((q) => `<button class="quick-add-btn" data-label="${q}">${q}</button>`).join('');

  const addQuickField = (label: string) => {
    (document.getElementById('field-label') as HTMLInputElement).value = label;
    const lower = label.toLowerCase();
    let type = 'text';
    if (lower === 'signature' || lower.includes('sign')) type = 'signature';
    else if (lower === 'date' || lower.includes('date')) type = 'date';
    else if (lower.includes('$') || lower.includes('amount') || lower.includes('contract') || lower.includes('tip')) type = 'currency';
    else if (lower === 'notes' || lower.includes('scope') || lower.includes('description') || lower.includes('list')) type = 'textarea';
    else if (lower.includes('number') || lower.includes('#') || lower.includes('hours')) type = 'number';
    (document.getElementById('field-type') as HTMLSelectElement).value = type;
    document.getElementById('options-row')!.style.display = 'none';
    addField(fields);
  };

  quickAddEl.querySelectorAll('.quick-add-btn').forEach((btn) => {
    btn.addEventListener('click', () => addQuickField(btn.getAttribute('data-label')!));
  });

  document.getElementById('add-field-btn')!.addEventListener('click', () => addField(fields));

  // AI Template Generation
  document.getElementById('ai-gen-template-btn')!.addEventListener('click', async () => {
    const prompt = (document.getElementById('ai-template-prompt') as HTMLInputElement).value.trim();
    if (!prompt) { showToast('Describe the template you need', 'error'); return; }

    const btn = document.getElementById('ai-gen-template-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span> Generating…';

    const aiPrompt = `You are a document template designer for ${industry} businesses.
Generate a list of form fields for this document: "${prompt}"
${industry === 'construction' ? 'This is for a construction company. Do NOT include any tip or gratuity fields. Focus on contract, licensing, labor, materials, and compliance fields.' : ''}

Return ONLY a valid JSON array of field definitions:
[
  {
    "label": "Field Name",
    "type": "text|textarea|number|currency|date|select|signature",
    "required": true|false,
    "placeholder": "Hint text",
    "width": "full|half",
    "options": ["Option A", "Option B"] // only for select type
  }
]

Generate 6-12 fields that make sense for this document type. Return only the JSON array.`;

    try {
      const api = (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
      const result = await api.sendToAI({
        prompt: aiPrompt,
        documentTitle: 'Template Generation',
        provider: 'huggingface',
        model: 'mistral-7b',
      }) as { aiResponse: string };

      const match = result.aiResponse.match(/\[[\s\S]*\]/);
      if (match) {
        const generated = JSON.parse(match[0]) as Array<{
          label: string; type: string; required?: boolean; placeholder?: string; width?: string; options?: string[];
        }>;

        let added = 0;
        for (const f of generated) {
          if (!f.label) continue;
          (document.getElementById('field-label') as HTMLInputElement).value = f.label;
          (document.getElementById('field-type') as HTMLSelectElement).value = (f.type as FieldType) || 'text';
          (document.getElementById('field-placeholder') as HTMLInputElement).value = f.placeholder ?? '';
          (document.getElementById('field-width') as HTMLSelectElement).value = f.width ?? 'full';
          (document.getElementById('field-required') as HTMLInputElement).checked = f.required !== false;
          if (f.type === 'select' && f.options) {
            (document.getElementById('field-options') as HTMLTextAreaElement).value = f.options.join('\n');
            document.getElementById('options-row')!.style.display = 'block';
          }
          addField(fields);
          added++;
        }

        // Auto-fill template name from prompt
        const nameEl = document.getElementById('tmpl-name') as HTMLInputElement;
        if (!nameEl.value) nameEl.value = prompt.replace(/\b\w/g, (c) => c.toUpperCase());

        showToast(`${added} fields generated — review and save`, 'success');
      } else {
        throw new Error('No JSON found');
      }
    } catch {
      showToast('AI generation unavailable — using quick-add instead', 'info');
    }

    btn.disabled = false;
    btn.innerHTML = '<i class="bi bi-cpu"></i> Generate Fields';
  });

  document.getElementById('save-btn')!.addEventListener('click', () => saveTemplate(fields, navigate));
}

function addField(fields: TemplateField[]): void {
  const label = (document.getElementById('field-label') as HTMLInputElement).value.trim();
  if (!label) { showToast('Enter a field label', 'error'); return; }

  const type = (document.getElementById('field-type') as HTMLSelectElement).value as FieldType;
  const optionsRaw = (document.getElementById('field-options') as HTMLTextAreaElement).value;
  const options = type === 'select' ? optionsRaw.split('\n').map((o) => o.trim()).filter(Boolean) : undefined;

  const field: TemplateField = {
    id: crypto.randomUUID(),
    label,
    type,
    required: (document.getElementById('field-required') as HTMLInputElement).checked,
    placeholder: (document.getElementById('field-placeholder') as HTMLInputElement).value.trim() || undefined,
    width: (document.getElementById('field-width') as HTMLSelectElement).value as 'full' | 'half',
    options,
  };

  fields.push(field);
  renderFieldList(fields);

  // Reset inputs
  (document.getElementById('field-label') as HTMLInputElement).value = '';
  (document.getElementById('field-placeholder') as HTMLInputElement).value = '';
  (document.getElementById('field-options') as HTMLTextAreaElement).value = '';
}

function renderFieldList(fields: TemplateField[]): void {
  const list = document.getElementById('field-list')!;
  document.getElementById('field-count')!.textContent = `${fields.length} field${fields.length !== 1 ? 's' : ''}`;

  if (fields.length === 0) {
    list.innerHTML = `<div class="empty-state" style="padding:24px"><div style="font-size:13px;color:var(--text-muted)">Add fields from the panel →</div></div>`;
    return;
  }

  const typeIcon: Record<string, string> = {
    text: 'T', textarea: '¶', number: '#', currency: '$', date: '📅', select: '▾', signature: '✍',
  };

  list.innerHTML = fields.map((f, i) => `
    <div class="field-row" style="display:flex;align-items:center;gap:10px;padding:10px 14px;background:var(--bg-primary);border:1px solid var(--border);border-radius:var(--radius-sm)">
      <div style="width:28px;height:28px;background:var(--brand-blue);border-radius:4px;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0">${typeIcon[f.type] ?? 'T'}</div>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:600">${escHtml(f.label)}</div>
        <div style="font-size:10px;color:var(--text-muted)">${f.type}${f.width === 'half' ? ' · half width' : ''}${f.required ? ' · required' : ''}</div>
      </div>
      <button class="btn btn-ghost btn-sm" style="padding:4px 8px;color:var(--danger)" data-remove="${i}">✕</button>
    </div>
  `).join('');

  list.querySelectorAll('[data-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.getAttribute('data-remove')!);
      fields.splice(idx, 1);
      renderFieldList(fields);
    });
  });
}

async function saveTemplate(fields: TemplateField[], navigate: NavigateFn): Promise<void> {
  const name = (document.getElementById('tmpl-name') as HTMLInputElement).value.trim();
  if (!name) { showToast('Enter a template name', 'error'); return; }
  if (fields.length === 0) { showToast('Add at least one field', 'error'); return; }

  const session = getSession();
  const payload = {
    businessId: session?.businessId ?? 'preview',
    name,
    industry: (document.getElementById('tmpl-industry') as HTMLSelectElement).value,
    category: (document.getElementById('tmpl-category') as HTMLInputElement).value.trim() || 'Custom',
    description: (document.getElementById('tmpl-desc') as HTMLInputElement).value.trim(),
    fields,
    isPreset: false,
  };

  try {
    const api = getAPI();
    await api.createTemplate(payload);
    showToast('Template saved', 'success');
    navigate('templates');
  } catch {
    showToast('Template saved (preview mode)', 'info');
    navigate('templates');
  }
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
